import {z} from 'zod';
import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import {IDataCatalog, updateDataCatalog, VerdocsEndpoint} from '@verdocs/js-sdk';
import {Button, Spinner, TextInput} from '../Components';
import {Dialog} from './Dialog';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  description: z.string().trim(),
});

type SchemaType = z.infer<typeof schema>;

interface EditDataCatalogDialogProps {
  catalog: IDataCatalog;
  onClose: () => void;
  onSaved: () => void;
}

export const EditDataCatalogDialog: FC<EditDataCatalogDialogProps> = ({catalog, onClose, onSaved}) => {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {name: catalog.name, description: catalog.description || ''},
  });

  const onSubmit = (data: SchemaType) => {
    // Tolerate double clicks
    if (submitting) {
      return;
    }

    setSubmitting(true);
    updateDataCatalog(VerdocsEndpoint.getDefault(), catalog.organization_id, catalog.id, {
      name: data.name,
      description: data.description || null,
    })
      .then(() => {
        setSubmitting(false);
        onSaved();
      })
      .catch((e: any) => {
        setSubmitting(false);
        toast.error(`Error saving the catalog: ${e.response?.data?.message || e.message}`);
      });
  };

  return (
    <Dialog onCancel={onClose}>
      <div className="mb-4 text-lg font-medium text-[#33364b]">Catalog details</div>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <TextInput label="Name" name="name" required />
          <TextInput label="Description" name="description" placeholder="What these keys describe" />

          <div className="mt-2 flex flex-row items-center justify-end gap-2 border-t border-[#f2f3f5] pt-4">
            {submitting && <Spinner mode="dark" />}
            <Button type="button" size="small" variant="secondary" label="Cancel" onClick={onClose} disabled={submitting} />
            <Button size="small" label="Save" type="submit" disabled={submitting} />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
